import { AxiosResponse } from 'axios';
import { SCHOOL_API_ENDPOINT, KITCHEN_API_ENDPOINT, SCHOOL_TYPE_API_ENDPOINT, SCHOOL_LOCATION_TYPE_API_ENDPOINT, SCHOOL_AREA_TYPE_API_ENDPOINT } from '../../../constants/constants';
import axiosInstance from '../api/apiInterceptor';
import { School } from '../types/school';

interface FetchSchoolsParams {
  stateId?: number;
  districtId?: number;
  blockAreaId?: number;
  villageWardId?: number;
  name?: string;
  address?: string;
  phone?: string;
  schoolCode?: string;
  page?: number;
  pageSize?: number;
  order?: string;
  sortBy?: string;
  search?: string;
}

export interface SchoolResponse {
  data: School[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SchoolType {
  id: number;
  name: string;
}

export interface SchoolLocationType {
  id: number;
  name: string;
}

export interface SchoolAreaType {
  id: number;
  name: string;
}

export interface KitchenType {
  id: number;
  name: string;
}

export const fetchAllSchools = async (): Promise<School[]> => {
  const response = await axiosInstance.get(`${ SCHOOL_API_ENDPOINT }`);
  return response.data.data;
};

export const getSchoolById = async (id: number): Promise<School | null> => {
  const response = await axiosInstance.get(`${ SCHOOL_API_ENDPOINT }/${ id }`);
  return response.data.data;
};

// Updated fetchSchoolApi with pagination and server-side filtering
export const fetchSchoolApi = async (params?: FetchSchoolsParams): Promise<SchoolResponse> => {
  try {
    let url = SCHOOL_API_ENDPOINT;

    const queryParams: string[] = [];

    // Add pagination parameters
    if (params?.page) {
      queryParams.push(`page=${params.page}`);
    }
    if (params?.pageSize) {
      queryParams.push(`pageSize=${params.pageSize}`);
    }

    if (params?.order) {
      queryParams.push(`order=${params.order}`);
    }
    if (params?.sortBy) {
      queryParams.push(`sortBy=${params.sortBy}`);
    }
    if (params?.search) {
      queryParams.push(`search=${params.search}`);
    }

    // Add filter parameters
    const filterParams: string[] = [];

    if (params?.stateId) {
      filterParams.push(`stateId|${params.stateId}`);
    }

    if (params?.districtId) {
      filterParams.push(`districtId|${params.districtId}`);
    }

    if (params?.blockAreaId) {
      filterParams.push(`blockAreaId|${params.blockAreaId}`);
    }

    if (params?.villageWardId) {
      filterParams.push(`villageWardId|${params.villageWardId}`);
    }

    if (params?.name) {
      filterParams.push(`name|${params.name}`);
    }

    if (params?.address) {
      filterParams.push(`address|${params.address}`);
    }

    if (params?.phone) {
      filterParams.push(`phone|${params.phone}`);
    }

    if (params?.schoolCode) {
      filterParams.push(`schoolCode|${params.schoolCode}`);
    }

    if (filterParams.length > 0) {
      queryParams.push(`filter=${filterParams.join(',')}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    const response: AxiosResponse<SchoolResponse> = await axiosInstance.get(url);

    return response.data || {
      data: [],
      total: 0,
      page: 1,
      pageSize: 10,
      totalPages: 0
    };

  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch schools');
  }
};

// School creation
export const createSchoolApi = async (schoolData: Omit<School, 'id'>): Promise<School> => {
  try {
    const response: AxiosResponse<{ data: School }> = await axiosInstance.post(SCHOOL_API_ENDPOINT, schoolData);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to create school');
  }
};

// School update
export const updateSchoolApi = async (id: number, schoolData: Partial<School>): Promise<School> => {
  try {
    const response: AxiosResponse<{ data: School }> = await axiosInstance.patch(`${SCHOOL_API_ENDPOINT}/${id}`, schoolData);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update school');
  }
};

// School deletion
export const deleteSchoolApi = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`${SCHOOL_API_ENDPOINT}/${id}`);
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to delete school');
  }
};

// Kitchen types
export const fetchKitchenTypesApi = async (): Promise<KitchenType[]> => {
  try {
    const response: AxiosResponse<{ data: KitchenType[] }> = await axiosInstance.get(KITCHEN_API_ENDPOINT);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch kitchen types');
  }
};

// School types
export const fetchSchoolTypesApi = async (): Promise<SchoolType[]> => {
  try {
    const response: AxiosResponse<{ data: SchoolType[] }> = await axiosInstance.get(SCHOOL_TYPE_API_ENDPOINT);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch school types');
  }
};

// School location types
export const fetchSchoolLocationTypesApi = async (): Promise<SchoolLocationType[]> => {
  try {
    const response: AxiosResponse<{ data: SchoolLocationType[] }> = await axiosInstance.get(SCHOOL_LOCATION_TYPE_API_ENDPOINT);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch school location types');
  }
};

// School area types
export const fetchSchoolAreaTypesApi = async (): Promise<SchoolAreaType[]> => {
  try {
    const response: AxiosResponse<{ data: SchoolAreaType[] }> = await axiosInstance.get(SCHOOL_AREA_TYPE_API_ENDPOINT);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch school area types');
  }
};
